<template>
    <div class="templates-container" :class="[$mq]">
        <div class="tool-selector-container">
            <select v-model="selectedTool" @change="onToolChange" class="tool-select">
                <option value="tasks">{{ $t('config.templates.tools.tasks') }}</option>
                <option value="records">{{ $t('config.templates.tools.records') }}</option>
            </select>
        </div>

        <div class="search-container-wrapper">
            <SearchBar
                :search="searchValue"
                @on-search="handleSearch"
                @on-button-click="cleanSearch"
                :placeholder="$t('config.templates.search.placeholder') || 'Buscar por una plantilla'"
            />
        </div>

        <div class="templates-list" v-if="!loading && filteredTemplates.length > 0">
            <div
                v-for="template in filteredTemplates"
                :key="template.id"
                class="template-item"
                @click="handleTemplateClick(template)"
            >
                <div class="template-content">
                    <div class="template-header">
                        <div v-if="template._checklistCategoryInfo && template._checklistCategoryInfo.name" class="category-badge">
                            <span
                                class="category-dot"
                                :style="{ backgroundColor: template._checklistCategoryInfo.color || '#D4D4D4' }"
                            ></span>
                            <span class="category-text">{{ template._checklistCategoryInfo.name }}</span>
                        </div>
                    </div>
                    <div class="template-name">{{ template.name }}</div>
                </div>
                <i class="i-andy-chevron-right i-andy-m template-arrow"></i>
            </div>
        </div>

        <div class="empty-state-wrapper" v-if="filteredTemplates.length === 0 && !loading">
            <EmptyPage
                :title="searchValue ? ($t('config.templates.search.empty_title') || 'Sin resultados') : ($t('config.templates.empty.title') || 'Nada por aquí… por ahora')"
                :description="searchValue ? ($t('config.templates.search.empty') || 'No se encontraron plantillas personalizables') : ($t('config.templates.empty.description') || 'Las plantillas aparecerán aquí cuando estén disponibles.')"
            />
        </div>

        <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
        </div>
    </div>
</template>

<script>
/* global logError */
import SearchBar from '@/components/domain/ui/SearchBar.vue'
import EmptyPage from '@/components/layout/EmptyPage.vue'
import { normalizeFlagToBool } from '@/utils/flag'
import { canOpenEditItem } from '@/utils/templateCustom'

export default {
    name: 'Templates',
    components: {
        SearchBar,
        EmptyPage
    },
    data() {
        return {
            selectedTool: 'tasks',
            searchValue: '',
            templates: [],
            loadSeq: 0,
            loading: false,
            timerSearch: null
        }
    },
    computed: {
        isAuditor() {
            return this.$store.getters['loginUser/getAuditor']
        },
        filteredTemplates() {
            const list = this.templates || []
            if (!this.searchValue) {
                return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            }
            return list
        }
    },
    methods: {
        onToolChange() {
            if (this.timerSearch) {
                clearTimeout(this.timerSearch)
                this.timerSearch = null
            }
            this.searchValue = ''
            this.loadTemplates()
        },
        handleSearch(e) {
            const self = this
            if (this.timerSearch) {
                clearTimeout(this.timerSearch)
                this.timerSearch = null
            }
            this.timerSearch = setTimeout(() => {
                self.searchValue = e.target.value
                self.loadTemplates()
            }, 500)
        },
        cleanSearch() {
            this.searchValue = ''
            this.loadTemplates()
        },
        async loadTemplates() {
            const seq = ++this.loadSeq
            const toolSnapshot = this.selectedTool
            this.loading = true
            try {
                const params = {
                    type: toolSnapshot
                }
                if (this.searchValue && this.searchValue.trim() !== '') {
                    params.search = this.searchValue.trim()
                }

                await this.loadToolCategories(toolSnapshot)
                const templates = await this.$store.dispatch('templates/loadCustomizableTemplates', params)
                const list = Array.isArray(templates) ? templates : []
                if (seq !== this.loadSeq) return
                this.templates = list.map((tpl) => {
                    const info = this.resolveCategoryInfo(tpl, toolSnapshot)
                    return { ...tpl, _checklistCategoryInfo: info }
                })
            } catch (error) {
                logError('Error cargando plantillas personalizables:', error)
                if (seq === this.loadSeq) this.templates = []
            } finally {
                if (seq === this.loadSeq) this.loading = false
            }
        },
        resolveCategoryInfo(template, toolSnapshot) {
            const info = { name: '', color: '' }
            if (!template) return info

            // Si el template ya trae la categoría resuelta desde storage/backend, usarla.
            // Aceptar variantes comunes: checklistCategory, checklist_category, category.
            const fromChecklistCategory = template.checklistCategory || template.checklist_category || template.category || null
            if (fromChecklistCategory) {
                if (typeof fromChecklistCategory === 'string') {
                    const raw = fromChecklistCategory.trim()
                    if (!raw) return info

                    // Si parece ser un ID (numérico o base64-like) no lo mostramos como label.
                    // En ese caso seguimos con lookup por category_id.
                    const noSpaces = !/\s/.test(raw)
                    const looksNumericId = /^\d+$/.test(raw)
                    // Base64-like: normalmente contiene +, / o = (padding) en nuestros IDs.
                    const looksBase64LikeId = raw.length >= 16 && /[+/=]/.test(raw)
                    const sameAsCategoryId = template.category_id != null && String(template.category_id).trim() === raw
                    const looksLikeId = noSpaces && (looksNumericId || looksBase64LikeId || sameAsCategoryId)
                    if (!looksLikeId) {
                        info.name = raw
                        return info
                    }
                }
                if (typeof fromChecklistCategory === 'object' && !Array.isArray(fromChecklistCategory)) {
                    const name = fromChecklistCategory.name != null ? String(fromChecklistCategory.name).trim() : ''
                    const color = fromChecklistCategory.color != null ? String(fromChecklistCategory.color).trim() : ''
                    if (name) {
                        info.name = name
                        info.color = color
                        return info
                    }
                }
            }

            if (!template.category_id) return info
            const id = String(template.category_id).trim()
            if (!id) return info
            const getterKey = toolSnapshot === 'records' ? 'register/getCategories' : 'tasks/getCategories'
            const getCategory = this.$store.getters[getterKey]
            if (typeof getCategory !== 'function') return info
            const cat = getCategory(id)
            info.name = cat && cat.name ? String(cat.name) : ''
            info.color = cat && cat.color ? String(cat.color) : ''
            return info
        },
        async loadToolCategories(toolSnapshot) {
            try {
                if (toolSnapshot === 'records') {
                    await this.$store.dispatch('register/loadCategories')
                } else {
                    await this.$store.dispatch('tasks/loadCategories')
                }
            } catch (e) {
                // no-op
            }
        },
        async handleTemplateClick(template) {
            try {
                const data = await this.$store.dispatch('templates/loadTemplateItems', {
                    templateId: template.id
                })
                if (data && !this.canEditAnyItemInTemplate(data)) {
                    this.$snackbar.error({
                        message: this.$t('config.templates.items.info_message_no_editable'),
                        duration: 5000,
                        closeable: true
                    })
                    return
                }
            } catch (e) {
                // Si falla la carga, dejar pasar la navegación normal
            }
            this.$emit('template-selected', template.id)
            this.$router.push({
                name: 'TemplateItems',
                params: { templateId: template.id }
            })
        },
        canEditAnyItemInTemplate(data) {
            const cfg = (data && data.config) || {}
            const canEdit = normalizeFlagToBool(cfg.edit_items) || normalizeFlagToBool(cfg.conf_edit)
            const canCreate = normalizeFlagToBool(cfg.create_items) || normalizeFlagToBool(cfg.conf_create)
            const sections = Array.isArray(data.sections) ? data.sections : []
            const flatItems = sections.reduce((acc, s) => acc.concat(Array.isArray(s.items) ? s.items : []), [])
            return flatItems.some((item) => canOpenEditItem(item, canEdit, canCreate))
        }
    },
    created() {
        this.loadTemplates()
        this.$store.dispatch('templates/loadAllBaseItems')
        this.$store.dispatch('templates/loadBaseItemsFromType6Templates')
    },
    beforeDestroy() {
        if (this.timerSearch) {
            clearTimeout(this.timerSearch)
        }
    }
}
</script>

<style lang="scss" scoped>
.templates-container {
    padding: 8px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .tool-selector-container {
        width: 100%;

        .tool-select {
            @include border-radius(4px);
            @include font-size(ml);
            @include background($image: img('down_caret_neutro_s90.svg'), $position: center right 15px, $size: 10px);
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            font-family: $text-medium;
            color: var(--color-Neutral-500, #7A7A7A);
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 18px;
            background-color: #fff;
            border: 1px solid $neutro-t30;
            padding: 12px 40px 12px 15px;
            cursor: pointer;
            width: 100%;
            height: 45px;

            &:hover {
                border-color: $neutro-t20;
            }

            &:focus {
                outline: none;
                border-color: $main;
            }
        }
    }

    .search-container-wrapper {
        width: 100%;

        ::v-deep .search-container {
            padding-bottom: 0;

            .search {
                font-size: 20px;
            }
        }
    }

    .templates-list {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .empty-state-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        overflow: hidden;
        position: relative;

        ::v-deep .empty-container {
            height: auto;
            z-index: 1;

            .empty-page-content {
                align-items: center;
                text-align: center;

                .content-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .andy-face {
                    position: relative;
                    top: auto;
                    right: auto;
                    margin-bottom: 8px;
                }

                .content-message {
                    width: 100%;
                    max-width: 400px;
                    text-align: center;

                    .title-content {
                        text-align: center;
                    }

                    .description {
                        text-align: center;
                    }
                }

                .button-action {
                    display: none;
                }
            }
        }
    }

    .template-item {
        @include interaction();
        @include border-radius(6px);
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        background-color: #fff;
        border: 1px solid $neutro-t30;
        padding: 16px;
        cursor: pointer;
        transition: all 0.2s ease;

        @include bgHover(#fff);

        &:hover {
            border-color: $neutro-t20;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .template-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 0;
        }

        .template-header {
            @include display-flex();
            @include align-items(center);
            @include justify-content(space-between);
            width: 100%;
        }

        .category-badge {
            @include display-flex();
            @include align-items(center);
            gap: 6px;
            margin-bottom: 6px;

            .category-dot {
                width: 8px;
                height: 8px;
                @include border-radius(50%);
                background-color: #ff4444;
                display: inline-block;
            }

            .category-text {
                @include font-size(xs);
                font-family: $text-medium;
                color: $neutro-s60;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
        }

        .template-name {
            @include font-size(m);
            font-family: $text-bold;
            color: $neutro-s90;
            line-height: 1.4;
        }

        .template-arrow {
            color: $neutro-s60;
            margin-left: 16px;
        }
    }

    .loading-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 40px 0;

        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid $neutro-t30;
            border-top-color: $main;
            @include border-radius(50%);
            animation: spin 0.8s linear infinite;
        }
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

// Portrait styles
.templates-container.portrait {
    padding: 0;

    .search-container-wrapper {
        ::v-deep .search-container {
            padding-bottom: 0;

            .search {
                font-size: 20px;
                padding: 0 60px 0 45px;
            }
        }
    }

    .empty-state-wrapper {
        ::v-deep .empty-container {
            .empty-page-content {
                .content-message {
                    max-width: 100%;
                    width: 100%;

                    .title-content {
                        font-size: 20px;
                    }

                    .description {
                        font-size: 16px;
                    }
                }
            }
        }
    }
}
</style>
